import React, { useState } from "react";
import { Box, Flex, Text, Badge, VStack, HStack, Spinner } from "@chakra-ui/react";
import { ScenariosProcessingOutput, ScenarioResultProto_ScenarioStatus, scenarioResultProto_ScenarioStatusToJSON } from "build-ts/schedule";
import { ScenarioResult } from "containers/agentChat/ReasoningTreeContainer";
import { AnalysisTreeNodeProto } from "build-ts/analysis";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import { ChatResponse_Artifact_ArtifactType } from "build-ts/chat";
import { useAgentChat } from "context/AgentChatContext";
import { ArtifactProto } from "build-ts/chat_session";


interface ReasoningTreeProps {
	treeNodes?: ScenarioResult[];
	nodeDataMap?: Map<string, AnalysisTreeNodeProto | null>;
	synthesisResult?: ScenariosProcessingOutput;
	contextualisationResult?: ScenariosProcessingOutput;
	onTableClick?: (nodeId: string) => void;
	onFindingsClick?: (scenarioId: number) => void;
	onSynthesisClick?: () => void;
	onContextualizationClick?: () => void;
	isLoadingSteps?: boolean;
	artifacts?: ArtifactProto[];
}

interface CollapsibleSectionProps {
	title: string;
	children: React.ReactNode;
	defaultExpanded?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ 
	title, 
	children, 
	defaultExpanded = false 
}) => {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	return (
		<Box mb={3}>
			<Flex 
				align="center" 
				cursor="pointer" 
				p={2} 
				bg="bg.surface" 
				_hover={{ bg: "purple.50" }} 
				borderRadius="sm" 
				transition="background-color 0.2s"
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<Box mr={2} color="purple.400">
					{isExpanded ? <AddIcon boxSize="10px"  color="purple.400"/> : <MinusIcon boxSize="10px" color="purple.400"/>}
				</Box>
				<Text fontSize="sm" fontWeight="semibold" color="purple.700" textAlign="left" mb={0}>{title}</Text>
			</Flex>
			{isExpanded && (
				<Box mt={2} ml={5} borderLeft="1px" borderColor="purple.200" pl={3}>
					{children}
				</Box>
			)}
		</Box>
	);
};

const getStatusColorScheme = (status?: ScenarioResultProto_ScenarioStatus) => {
	switch (status) {
		case ScenarioResultProto_ScenarioStatus.VALID:
			return "green";
		case ScenarioResultProto_ScenarioStatus.INVALID:
			return "red";
		case ScenarioResultProto_ScenarioStatus.WARNING:
			return "yellow";
		case ScenarioResultProto_ScenarioStatus.ERROR:
			return "red";
		default:
			return "gray";
	}
};

const convertArtifactsTypeToLabel = (artifactType: ChatResponse_Artifact_ArtifactType | undefined) => {
	if(artifactType){
		switch(artifactType){
			case ChatResponse_Artifact_ArtifactType.REFERENCED_DATA:
				return "Referenced Data";
			default:
				return "Unknown Artifact Type";
		}
	}
	return "";
};

const ReasoningTree: React.FC<ReasoningTreeProps> = ({ 
	treeNodes,
	nodeDataMap,
	synthesisResult,
	contextualisationResult,
	onTableClick,
	onFindingsClick,
	onSynthesisClick,
	onContextualizationClick,
	isLoadingSteps,
	artifacts
}) => {
	const { listOfNodes } = useAgentChat();
	return (
		<Box p={3} 
			bg="bg.surface" 
			height="100%"
			width="100%"
			overflowY="auto"
		>
			{isLoadingSteps ? (
				<Box display="flex" alignItems="left" justifyContent="left" mt={4}>
					<Spinner size="sm" color="purple.700" />
					<Text fontSize="sm" fontWeight="semibold" color="gray.500" m={0} ml={2}>Loading Steps...</Text>
				</Box>
			) : (
				<>
					{/* Tree Nodes - Grouped under Scenario */}
					{treeNodes && treeNodes.length > 0 && (
						<CollapsibleSection
							title={`Scenario (${treeNodes.length})`}
							defaultExpanded={true}
						>
							<VStack spacing={0} align="stretch">
								{treeNodes.map((node, index) => (
									<CollapsibleSection
										key={node.id || index}
										title={node.issue || `Scenario ${node.id}`}
										defaultExpanded={false}
									>
										<VStack spacing={0} align="stretch">
											{/* Data Tables Used */}
											{node.referencedTreeNodes && node.referencedTreeNodes.length > 0 && (
												<VStack spacing={0} align="stretch">
													<CollapsibleSection
														title={"Data Tables Used"}
														defaultExpanded={false}
													>
														{node.referencedTreeNodes.map((treeNode, treeIdx) => 
															treeNode.node_ids.map((nodeId, nodeIdx) => {
																const nodeData = listOfNodes?.get(nodeId);
																return (
																	<Box 
																		key={`${treeIdx}-${nodeIdx}-${nodeId}`}
																		p={2} 
																		bg="purple.50" 
																		borderRadius="sm" 
																		border="1px" 
																		borderColor="bg.surface"
																		cursor="pointer"
																		w="fit-content"
																		_hover={{ bg: "purple.100", borderColor: "purple.200" }}
																		onClick={() => onTableClick?.(nodeId)}
																	>
																		<Text fontSize="xs" color="purple.700" textAlign="left" m={0}>
																			{nodeData?.node_label || nodeId}
																		</Text>
																	</Box>
																);
															})
														)}
													</CollapsibleSection>
												</VStack>
											)}

											{/* Status */}
											{node.status !== undefined && (
												<HStack spacing={2} mb={2} align="center">
													<Text fontSize="sm" fontWeight="semibold" color="purple.700" m={0}>Status:</Text>
													<Badge 
														colorScheme={getStatusColorScheme(node.status)}
														px={2} 
														py={1} 
														borderRadius="sm" 
														fontSize="xs"
													>
														{scenarioResultProto_ScenarioStatusToJSON(node.status)}
													</Badge>
												</HStack>
											)}

											{/* Findings */}
											{node.findings && (
												<Box mb={2}>
													
													<Box 
														p={2} 
														bg="purple.50" 
														borderRadius="sm" 
														border="1px" 
														borderColor="bg.surface"
														w="fit-content"
														cursor="pointer"
														_hover={{ bg: "purple.100", borderColor: "purple.200" }}
														onClick={() => onFindingsClick?.(node.id!)}
													>
														<Text fontSize="sm" fontWeight="semibold" color="purple.700" textAlign="left" m={0}>
															Findings
														</Text>
													</Box>
												</Box>
											)}
										</VStack>
									</CollapsibleSection>
								))}
							</VStack>
						</CollapsibleSection>
					)}

					{/* Synthesis Result */}
					{synthesisResult && (
						<CollapsibleSection
							title="Synthesis Result"
							defaultExpanded={false}
						>
							<VStack spacing={1} align="stretch">
								{synthesisResult.notify !== undefined && (
									<HStack spacing={2} align="center">
										<Text fontSize="sm" fontWeight="semibold" color="purple.700" m={0}>Notify:</Text>
										<Badge 
											colorScheme={synthesisResult.notify ? "green" : "red"}
											px={1} 
											py={0} 
											borderRadius="sm" 
											fontSize="xs"
										>
											{synthesisResult.notify ? "Yes" : "No"}
										</Badge>
									</HStack>
								)}
								{synthesisResult.output_string && (
									<Box>
										<Box 
											p={1} 
											bg="purple.50" 
											borderRadius="sm" 
											border="1px" 
											borderColor="bg.surface"
											w="fit-content"
											cursor="pointer"
											_hover={{ bg: "purple.100", borderColor: "purple.200" }}
											onClick={() => onSynthesisClick?.()}
										>
											<Text fontSize="sm" fontWeight="semibold" color="purple.700" textAlign="left" m={0}>
												Output
											</Text>
										</Box>
									</Box>
								)}
							</VStack>
						</CollapsibleSection>
					)}

					{/* Contextualization Result */}
					{contextualisationResult && (
						<CollapsibleSection
							title="Contextualization Result"
							defaultExpanded={false}
						>
							<VStack spacing={1} align="stretch">
								{contextualisationResult.notify !== undefined && (
									<HStack spacing={2} align="center">
										<Text fontSize="sm" fontWeight="semibold" color="purple.700" m={0}>Notify:</Text>
										<Badge 
											colorScheme={contextualisationResult.notify ? "green" : "red"}
											px={1} 
											py={0} 
											borderRadius="sm" 
											fontSize="xs"
										>
											{contextualisationResult.notify ? "Yes" : "No"}
										</Badge>
									</HStack>
								)}
								{contextualisationResult.output_string && (
									<Box>
										<Box 
											p={1} 
											bg="purple.50" 
											borderRadius="sm" 
											border="1px" 
											borderColor="bg.surface"
											w="fit-content"
											cursor="pointer"
											_hover={{ bg: "purple.100", borderColor: "purple.200" }}
											onClick={() => onContextualizationClick?.()}
										>
											<Text fontSize="sm" fontWeight="semibold" color="purple.700" textAlign="left" m={0}>
												Output
											</Text>
										</Box>
									</Box>
								)}
							</VStack>
						</CollapsibleSection>
					)}
					{/* To-Do: Implement artifacts  according to the new chat session response and remove the above contextualization and synthesis result*/}
					
					{/* Artifacts */}
					{/* {artifacts && artifacts.length > 0 && (
							<VStack spacing={1} align="stretch">
								{artifacts.map((artifact, index) => (
									<>
									  {artifact.referenced_data && artifact.referenced_data.length > 0 && (
											<Box key={index}>
												<CollapsibleSection
													title={convertArtifactsTypeToLabel(chatResponse_Artifact_ArtifactTypeFromJSON(artifact?.artifact_type))}
													defaultExpanded={true}
												>
													<VStack spacing={1} align="stretch">
														{artifact?.referenced_data && artifact?.referenced_data.length > 0 && artifact?.referenced_data.map((referencedData, index) => (
															<CollapsibleSection
																title={`Data Tables Used (${referencedData.node_ids.length})`}
																defaultExpanded={false}
															>
																<VStack spacing={0} align="stretch">
																	{referencedData.node_ids.map((nodeId, idx) => {
																		const nodeData = listOfNodes?.get(nodeId);
																		return (
																			<Box 
																				key={nodeId} 
																				p={2} 
																				bg="purple.50" 
																				borderRadius="sm" 
																				border="1px" 
																				borderColor="bg.surface"
																				cursor="pointer"
																				w="fit-content"
																				_hover={{ bg: "purple.100", borderColor: "purple.200" }}
																				onClick={() => onTableClick?.(nodeId)}
																			>
																				<Text fontSize="xs" color="purple.700" textAlign="left" m={0}>
																					{nodeData?.node_label || nodeId}
																				</Text>
																			</Box>
																		);
																	})}
																</VStack>
															</CollapsibleSection>
														))}
													</VStack>
												</CollapsibleSection>
											</Box>
										)}
									</>
								))}
							</VStack>
					)} */}

					{/* Empty State */}
					{(!treeNodes || treeNodes.length === 0) && !synthesisResult && !contextualisationResult && !artifacts && (
						<Box textAlign="left" py={6}>
							<Text color="gray.400" fontSize="sm">No reasoning tree data available</Text>
						</Box>
					)}
				</>
			)}
			

			{/* Empty State */}
			{(!treeNodes || treeNodes.length === 0) && !synthesisResult && !contextualisationResult && !artifacts && (
				<Box textAlign="left" py={6}>
					<Text color="gray.400" fontSize="sm">No reasoning tree data available</Text>
				</Box>
			)}
		</Box>
	);
};

export default ReasoningTree;